import { utc as momentUTC } from 'moment-timezone';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { DatePickerUI, Selector } from 'sonata-react-components';

import { useFlightCopies } from '@/components/ioSettings/InsertionOrderForm/FlightSettings/Flight/useFlightCopies';
import { DatePickerStyled } from './DatePicker.styled';
import {
  getAllTimeZones,
  getLineItemPastRules,
  LINE_ITEM_TIMEZONE,
  toLineItemDate,
  toPickerValue
} from './datePicker.helpers';
import { useLineItemPickerSchedules } from './useLineItemPickerSchedules';

import type { DateRangeValue } from 'sonata-react-components';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { LineItemSchedule } from '@/components/ioSettings/InsertionOrderForm/hooks/useIoLineItemSchedules';

const NO_LINE_ITEMS: LineItemSchedule[] = [];

type Props = {
  beginDate: Date | null;
  completed: boolean;
  endDate: Date | null;
  id: string;
  /** Flights of the line item's IO, painted (not blocking) in their colour; `undefined` while loading. */
  ioFlights?: InsertionOrderFlight[];
  /** Line items of the line item's IO, drawn as day lines (this one with its live dates). */
  ioLineItems?: LineItemSchedule[];
  /** Name of the line item being edited, for its tooltip row. */
  lineItemName?: string;
  onChangeLineItemDates?: (dates: { beginDate: Date | null; endDate: Date | null }) => void;
  onChangeTimezone?: (timezone: string) => void;
  timezone: string;
};

function DatePicker({
  beginDate,
  completed,
  endDate,
  id,
  ioFlights,
  ioLineItems = NO_LINE_ITEMS,
  lineItemName,
  onChangeLineItemDates,
  onChangeTimezone,
  timezone
}: Props) {
  const { formatMessage } = useIntl();
  const copies = useFlightCopies();

  const { markers, occupiedRanges } = useLineItemPickerSchedules(ioFlights, ioLineItems, {
    _id: id,
    beginDate,
    endDate,
    name: lineItemName ?? ''
  });

  const handleDatesChange = (dates: DateRangeValue) => {
    const { endDate: newEndDate, startDate: newStartDate } = dates;
    onChangeLineItemDates?.({ beginDate: toLineItemDate(newStartDate), endDate: toLineItemDate(newEndDate) });
  };

  // Only a draft whose beginDate has already passed needs correcting on mount.
  // Reporting the dates back unchanged in every other case would mark the
  // entity as having unsaved changes just for opening the form.
  useEffect(() => {
    const today = momentUTC().startOf('day');
    if (!beginDate || completed || !today.isAfter(momentUTC(beginDate))) {
      return;
    }
    const endDateHasPassed = endDate && today.isAfter(momentUTC(endDate));
    handleDatesChange({
      endDate: endDateHasPassed ? momentUTC() : endDate ? momentUTC(endDate) : null,
      startDate: momentUTC()
    });
  }, []);

  const handleChangeTimeZone = (newTimezone: Record<string, any>) => {
    onChangeTimezone?.(newTimezone.value);
  };

  const pastRules = getLineItemPastRules(completed, beginDate, endDate);

  return (
    <DatePickerStyled className="datePicker">
      <div className="datePicker__titleContainer">
        <div className="datePicker__title">
          {formatMessage({
            defaultMessage: 'Specify when and where your campaign will run',
            id: 'datePicker.title'
          })}
        </div>
      </div>
      <div className="datePicker__inputContainer">
        <div className="datePicker__infoContainer">
          <DatePickerUI
            clearable={!pastRules.disabled}
            endPlaceholder="End date"
            id="line_item_schedule_input"
            label={formatMessage({
              defaultMessage: 'Date range selection',
              id: 'datePicker.dateRange'
            })}
            // coverage is only checked once the flights are loaded (no flashing "!" while they load)
            markerCoverageWarning={ioFlights ? copies.markerCoverageWarning : undefined}
            markerName={copies.markerName}
            markers={markers}
            minNights={0}
            mode="range"
            occupiedRanges={occupiedRanges}
            onChange={handleDatesChange}
            selectionMustBeCovered={Boolean(ioFlights)}
            startPlaceholder="Begin date"
            timezone={LINE_ITEM_TIMEZONE}
            value={toPickerValue(beginDate, endDate)}
            {...pastRules}
          />
        </div>
        <div className="datePicker__infoContainer">
          <Selector
            className="timezone-select"
            defaultValue={[
              {
                id: timezone,
                label: timezone
              }
            ]}
            isDisabled={Boolean(pastRules.disabled)}
            label={formatMessage({
              defaultMessage: 'Time zone selection',
              id: 'datePicker.timezone'
            })}
            onChange={handleChangeTimeZone}
            options={getAllTimeZones()}
            type="simple"
          />
        </div>
      </div>
    </DatePickerStyled>
  );
}

export default DatePicker;
